import React, { useState, useMemo } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatMoney } from "../utils/utils";
import GdTable from "./components/GdTable";
/* global gdAlertify */

const BackfillCobranzaAplicaciones = () => {
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [resultado, setResultado] = useState(null);

    const filas = useMemo(() => {
        if (!resultado?.discrepancias) return [];
        const rows = [];
        Object.entries(resultado.discrepancias).forEach(([pacienteId, items]) => {
            items.forEach(item => {
                rows.push({
                    paciente_id: pacienteId,
                    ...item,
                    diferencia: Number(item.monto_pagado_simulado) - Number(item.monto_pagado_actual)
                });
            });
        });
        return rows;
    }, [resultado]);

    const columns = [
        { title: "Paciente ID", name: "paciente_id" },
        { title: "Tipo Deuda", name: "tipo_deuda" },
        { title: "ID Deuda", name: "id" },
        { title: "Referencia", name: "referencia" },
        { title: "Pagado Actual", name: "monto_pagado_actual", render: row => `$${formatMoney(row.monto_pagado_actual)}` },
        { title: "Pagado Simulado (FIFO)", name: "monto_pagado_simulado", render: row => `$${formatMoney(row.monto_pagado_simulado)}` },
        {
            title: "Diferencia",
            name: "diferencia",
            render: row => (
                <span className={row.diferencia !== 0 ? "text-danger fw-bold" : ""}>
                    ${formatMoney(row.diferencia)}
                </span>
            )
        }
    ];

    const ejecutarDryRun = async () => {
        setLoading(true);
        try {
            const resp = await axiosInstance.get("/acuerdo-mutuo/cartola-pacientes/backfill-aplicaciones", { params: { dry_run: 1 } });
            setResultado(resp.data);
            gdAlertify.notiSuccess("Simulación completada.");
        } catch (error) {
            console.error(error);
            gdAlertify.notiError("Error al ejecutar la simulación.");
        } finally {
            setLoading(false);
        }
    };

    const aplicarBackfill = () => {
        gdAlertify.confirm({
            title: "¿Aplicar backfill?",
            message: (resultado?.pacientes_con_discrepancia > 0)
                ? `Hay ${resultado.pacientes_con_discrepancia} paciente(s) con discrepancias. Esto va a recalcular y sobrescribir monto_pagado/saldo con el resultado del FIFO. ¿Continuar de todas formas?`
                : "Esto va a reconstruir cobranza_aplicaciones para todos los pacientes con pagos activos. ¿Continuar?",
            onConfirm: async () => {
                setApplying(true);
                try {
                    const resp = await axiosInstance.get("/acuerdo-mutuo/cartola-pacientes/backfill-aplicaciones", { params: { dry_run: 0 } });
                    setResultado(resp.data);
                    gdAlertify.notiSuccess("Backfill aplicado con éxito.");
                } catch (error) {
                    console.error(error);
                    gdAlertify.notiError("Error al aplicar el backfill.");
                } finally {
                    setApplying(false);
                }
            },
            onCancel: () => {}
        });
    };

    return (
        <div className="container-fluid py-4">
            <h4 className="fw-bold mb-2"><i className="bi bi-database-gear me-2"></i>Backfill Cobranza Aplicaciones</h4>
            <p className="text-muted">
                Herramienta de una sola vez. Simula el FIFO sobre el historial actual de pagos/deudas y lo compara contra lo
                guardado hoy en <code>monto_pagado</code>. Corre primero la simulación; si no hay discrepancias, aplica el
                backfill para poblar <code>cobranza_aplicaciones</code>.
            </p>

            <div className="d-flex gap-2 mb-4">
                <button className="btn btn-primary" onClick={ejecutarDryRun} disabled={loading || applying}>
                    {loading ? (
                        <><span className="spinner-border spinner-border-sm me-2"></span>Simulando...</>
                    ) : (
                        <><i className="bi bi-play-circle me-2"></i>Ejecutar Simulación (Dry-Run)</>
                    )}
                </button>
                <button className="btn btn-success" onClick={aplicarBackfill} disabled={!resultado || loading || applying}>
                    {applying ? (
                        <><span className="spinner-border spinner-border-sm me-2"></span>Aplicando...</>
                    ) : (
                        <><i className="bi bi-check-circle me-2"></i>Aplicar Backfill</>
                    )}
                </button>
            </div>

            {resultado && (
                <>
                    <div className="row g-3 mb-4">
                        <div className="col-md-3">
                            <div className="card shadow-sm border-0 bg-light h-100">
                                <div className="card-body text-center">
                                    <h6 className="text-muted small fw-bold mb-1">Modo</h6>
                                    <h5 className="mb-0">{resultado.dry_run ? "Simulación" : "Aplicado"}</h5>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card shadow-sm border-0 bg-light h-100">
                                <div className="card-body text-center">
                                    <h6 className="text-muted small fw-bold mb-1">Pacientes Evaluados</h6>
                                    <h5 className="mb-0">{resultado.pacientes_evaluados}</h5>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card shadow-sm border-0 bg-light h-100 border-start border-success border-4">
                                <div className="card-body text-center">
                                    <h6 className="text-muted small fw-bold mb-1">OK</h6>
                                    <h5 className="mb-0 text-success">{resultado.pacientes_ok}</h5>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="card shadow-sm border-0 bg-light h-100 border-start border-danger border-4">
                                <div className="card-body text-center">
                                    <h6 className="text-muted small fw-bold mb-1">Con Discrepancia</h6>
                                    <h5 className="mb-0 text-danger">{resultado.pacientes_con_discrepancia}</h5>
                                </div>
                            </div>
                        </div>
                    </div>

                    {filas.length > 0 ? (
                        <GdTable
                            columns={columns}
                            data={filas}
                            stickyHeader={true}
                            height={500}
                            headerHeight={50}
                            showCsvExport={true}
                        />
                    ) : (
                        <div className="alert alert-success mb-0">
                            <i className="bi bi-check-circle me-2"></i>Sin discrepancias.
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default BackfillCobranzaAplicaciones;
